import { useCallback } from 'react'
import { IconSearch, IconWarningTrOutline } from '@holbonkit/icons'
import { Box, Button, Skeleton, Text, Title } from '@holbonkit/ui'
import { useNavigate } from '@tanstack/react-router'
import { clsx } from 'clsx'

import type { SelfEmployedTransactionListItem } from '@/shared/api'
import { navigateToDriverAppCity } from '@/shared/lib/navigate'

import { OperationsItem } from '../operations-item/operations-item'

import styles from './secondary-block.module.scss'

import { IllustrationSbpAccount } from '@/assets'

type TSecondaryBlockProps = {
    isError: boolean
    isLoading: boolean
    latestOperations: SelfEmployedTransactionListItem[] | undefined
    onConfirmSmz: () => void
    onOpenHistory: () => void
    refetch: () => void
    withdrawalIsAvailable: boolean
    withdrawalUnavailableReason?: string
}

export const SecondaryBlock = ({
    isError,
    isLoading,
    latestOperations,
    onConfirmSmz,
    onOpenHistory,
    refetch,
    withdrawalIsAvailable,
    withdrawalUnavailableReason,
}: TSecondaryBlockProps) => {
    const navigate = useNavigate()

    const handleNavigateToOrders = useCallback(() => {
        navigateToDriverAppCity()
    }, [])

    if (isError) {
        return <SecondaryBlockError refetch={refetch} />
    }

    if (isLoading) {
        return <SecondaryBlockLoading />
    }

    switch (withdrawalIsAvailable) {
        case false: {
            switch (withdrawalUnavailableReason) {
                case 'no_sbp_account': {
                    // нет счёта СБП
                    return (
                        <Box className={clsx(styles['operations'], styles['operations-empty'])}>
                            <img
                                alt='Empty'
                                className={styles['operations-empty__image']}
                                src={IllustrationSbpAccount}
                            />
                            <Box className={styles['operations-empty__text']}>
                                <Title variant='header-24-bold'>Добавьте счёт СБП</Title>
                                <Text variant='body-m-16-regular'>
                                    Сможете выводить деньги за заказы
                                    <br />с оплатой картой на добавленный
                                    <br />
                                    счёт
                                </Text>
                            </Box>
                            <Button
                                className={styles['operations-empty__button']}
                                size='sm'
                                variant='primary'
                                onClick={() => {
                                    navigate({ to: '/withdrawal-smz/account-link' })
                                }}
                            >
                                Добавить
                            </Button>
                        </Box>
                    )
                }

                // нет статуса самозанятости или статус утерян
                case 'not_self_employed':
                case 'self_employed_status_lost': {
                    return (
                        <Box className={clsx(styles['operations'], styles['operations-empty'])}>
                            <Box
                                className={clsx(
                                    styles['operations-empty__icon'],
                                    styles['operations-empty__icon--warning'],
                                )}
                            >
                                <IconWarningTrOutline
                                    color='var(--extensions-text-and-icon-error)'
                                    size={32}
                                />
                            </Box>
                            <Box className={styles['operations-empty__text']}>
                                <Title variant='header-24-bold'>
                                    Подтвердите
                                    <br />
                                    самозанятость
                                </Title>
                                <Text variant='body-m-16-regular'>
                                    Водители с самозанятостью могут
                                    <br />
                                    выводить деньги за заказы
                                    <br />с оплатой картой на свой счёт СБП
                                </Text>
                            </Box>
                            <Button
                                className={styles['operations-empty__button']}
                                size='sm'
                                variant='primary'
                                onClick={onConfirmSmz}
                            >
                                Подтвердить
                            </Button>
                        </Box>
                    )
                }
                // технические неполадки
                case 'fns_unavailable': {
                    return latestOperations?.length && latestOperations.length > 0 ?
                            // есть операции
                            <WithOperations
                                latestOperations={latestOperations}
                                onOpenHistory={onOpenHistory}
                            />
                            // нет операций
                        :   <WithoutOperations onNavigateToOrders={handleNavigateToOrders} />
                }

                default: {
                    return latestOperations?.length && latestOperations.length > 0 ?
                            // есть операции
                            <WithOperations
                                latestOperations={latestOperations}
                                onOpenHistory={onOpenHistory}
                            />
                            // нет операций
                        :   <WithoutOperations onNavigateToOrders={handleNavigateToOrders} />
                }
            }
        }
        case true: {
            return latestOperations?.length && latestOperations.length > 0 ?
                    // есть операции
                    <WithOperations
                        latestOperations={latestOperations ?? []}
                        onOpenHistory={onOpenHistory}
                    />
                    // нет операций
                :   <WithoutOperations onNavigateToOrders={handleNavigateToOrders} />
        }
    }
}

const SecondaryBlockError = ({ refetch }: { refetch: () => void }) => {
    return (
        <Box className={clsx(styles['operations'], styles['operations-empty'])}>
            <Box
                className={clsx(
                    styles['operations-empty__icon'],
                    styles['operations-empty__icon--warning'],
                )}
            >
                <IconWarningTrOutline color='var(--extensions-text-and-icon-error)' size={32} />
            </Box>
            <Box className={styles['operations-empty__text']}>
                <Title variant='header-24-bold'>Ошибка при загрузке операций</Title>
                <Text variant='body-m-16-regular'>
                    Возможно, произошла ошибка при загрузке операций. Попробовать снова.
                </Text>
            </Box>
            <Button
                className={styles['operations-empty__button']}
                size='sm'
                variant='primary'
                onClick={refetch}
            >
                Попробовать снова
            </Button>
        </Box>
    )
}

const SecondaryBlockLoading = () => {
    return (
        <Box className={styles['operations']}>
            <Box className={styles['operations__header']}>
                <Text variant='body-l-18-bold'>Последние операции</Text>
                <Text className={styles['operations__header--all']} variant='body-m-16-medium'>
                    Все
                </Text>
            </Box>
            <Box className={styles['operations__content']}>
                <Skeleton style={{ height: 40, marginBottom: 16, marginTop: 16 }} />
                <Skeleton style={{ height: 40, marginBottom: 16, marginTop: 16 }} />
                <Skeleton style={{ height: 40, marginBottom: 16, marginTop: 16 }} />
            </Box>
        </Box>
    )
}

type TWithOperationsProps = {
    latestOperations: SelfEmployedTransactionListItem[]
    onOpenHistory: () => void
}

const WithOperations = ({ latestOperations, onOpenHistory }: TWithOperationsProps) => {
    return (
        <Box className={styles['operations']}>
            <Box className={styles['operations__header']} onClick={onOpenHistory}>
                <Text variant='body-l-18-bold'>Последние операции</Text>
                <Text className={styles['operations__header--all']} variant='body-m-16-medium'>
                    Все
                </Text>
            </Box>
            <Box className={styles['operations__content']}>
                {latestOperations.map((operation) => (
                    <OperationsItem
                        key={operation.id}
                        amount={operation.amount}
                        date={operation.created_at}
                        id={operation.id}
                        status={operation.status}
                        type={operation.type}
                    />
                ))}
            </Box>
        </Box>
    )
}

type TWithoutOperationsProps = {
    onNavigateToOrders: () => void
}

const WithoutOperations = ({ onNavigateToOrders }: TWithoutOperationsProps) => {
    return (
        <Box className={clsx(styles['operations'], styles['operations-empty'])}>
            <Box
                className={clsx(
                    styles['operations-empty__icon'],
                    styles['operations-empty__icon--search'],
                )}
            >
                <IconSearch color='var(--text-and-icon-secondary)' size={32} />
            </Box>
            <Box className={styles['operations-empty__text']}>
                <Title variant='header-24-bold'>Операций пока нет</Title>
                <Text variant='body-m-16-regular'>Выполните заказ с оплатой картой</Text>
            </Box>
            <Button
                className={styles['operations-empty__button']}
                size='sm'
                variant='primary'
                onClick={onNavigateToOrders}
            >
                К заказам
            </Button>
        </Box>
    )
}
